import React from 'react'
import { Document, Image, Page, Text, View } from '@react-pdf/renderer'
import moment from 'moment'
import { CHARACTERISTICS } from '../incidentConstants'

// Built-in Helvetica only covers WinAnsi — stick to plain punctuation
// (em dash, bullet and middle dot are all fine; ≥/≤ and arrows are not).

const NAVY = '#1F3D7C'
const INK = '#0f172a'
const SLATE = '#475569'
const MUTED = '#94a3b8'
const LINE = '#CBD5E1'
const STRIPE = '#F5F7FA'
const TINT = '#EEF2FA'

const STATUS_COLORS = {
  New: '#0284c7',
  'In Progress': NAVY,
  Waiting: '#d97706',
  Blocked: '#e11d48',
  Completed: '#059669',
  'Closed – No Action Required': '#64748b',
}

const HARM_COLORS = {
  Harm: '#e11d48',
  'Near Miss': '#d97706',
  'No Harm': '#059669',
}

const fullName = (account) =>
  [account?.firstName, account?.lastName].filter(Boolean).join(' ')

const hasText = (value) => typeof value === 'string' && value.trim() !== ''

const fmtDateTime = (value, format = 'Do MMM YYYY [at] HH:mm') =>
  value ? moment(value).format(format) : null

// ---------------------------------------------------------------- primitives

const Pill = ({ label, color = NAVY, filled }) => (
  <View
    style={{
      borderWidth: 0.8,
      borderColor: color,
      backgroundColor: filled ? color : undefined,
      borderRadius: 3,
      paddingHorizontal: 5,
      paddingVertical: 1.5,
    }}
  >
    <Text style={{ fontSize: 7, color: filled ? '#FFFFFF' : color }}>{label}</Text>
  </View>
)

const Eyebrow = ({ children, color = MUTED }) => (
  <Text
    style={{
      fontSize: 6.5,
      color,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      marginBottom: 1.5,
    }}
  >
    {children}
  </Text>
)

// Section banner + body. Sections are always printed (with an "empty" line
// when nothing is recorded) so a reviewer can see what is still outstanding.
//
// Deliberately a fragment, so the banner and the body are direct children of
// the Page. react-pdf's pagination misbehaves on the first child of a nested
// View: minPresenceAhead is ignored there (the banner used to be stranded at
// the foot of a page), and a View whose contents are all pushed to the next
// page is left behind whole, squashed into the space that was left.
// `minPresenceAhead` is the room the banner needs below it to stay put.
const Section = ({ title, caption, minPresenceAhead = 60, children }) => (
  <>
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: NAVY,
        paddingVertical: 5,
        paddingHorizontal: 8,
        marginTop: 14,
        marginBottom: 4,
      }}
      wrap={false}
      minPresenceAhead={minPresenceAhead}
    >
      <Text style={{ fontSize: 10.5, color: '#FFFFFF' }}>{title}</Text>
      {caption && <Text style={{ fontSize: 7.5, color: '#C7D2E8' }}>{caption}</Text>}
    </View>
    {children}
  </>
)

const Empty = ({ children }) => (
  <Text style={{ fontSize: 8, color: MUTED, paddingHorizontal: 6, paddingVertical: 4 }}>
    {children}
  </Text>
)

// A labelled block of prose (multi-line preserved). Skipped when empty.
const Field = ({ label, value }) => {
  if (!hasText(value)) return null
  return (
    <View style={{ paddingHorizontal: 6, paddingVertical: 3 }} minPresenceAhead={24}>
      <Eyebrow>{label}</Eyebrow>
      <Text style={{ fontSize: 8.5, color: INK, lineHeight: 1.4 }}>{value.trim()}</Text>
    </View>
  )
}

// Compact key/value cell used in the summary grid.
const Cell = ({ label, value, color, width = '25%' }) => (
  <View style={{ width, paddingVertical: 4, paddingHorizontal: 6 }}>
    <Eyebrow>{label}</Eyebrow>
    <Text style={{ fontSize: 8.5, color: color ?? INK }}>{value || '—'}</Text>
  </View>
)

const HeaderRow = ({ columns }) => (
  <View
    style={{
      flexDirection: 'row',
      paddingVertical: 2,
      paddingHorizontal: 6,
      borderBottomWidth: 0.5,
      borderBottomColor: LINE,
      marginBottom: 1,
    }}
    wrap={false}
  >
    {columns.map(([label, flex]) => (
      <Text
        key={label}
        style={{
          fontSize: 6.5,
          flex,
          color: MUTED,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
        }}
      >
        {label}
      </Text>
    ))}
  </View>
)

const Row = ({ children, striped }) => (
  <View
    style={{
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 3,
      paddingHorizontal: 6,
      backgroundColor: striped ? STRIPE : undefined,
    }}
    wrap={false}
  >
    {children}
  </View>
)

const cellText = (flex, extra) => ({ fontSize: 8, flex, paddingRight: 4, ...extra })

// Boxed card for witness statements and notes. The card has to be allowed to
// wrap: an unbreakable card taller than a page cannot be moved anywhere it
// fits, so it is squashed into the space left on the page, with its text
// running over the heading, past the border and off the page. Only the head
// (name, date, footer) is kept together, and minPresenceAhead stops it being
// stranded at the foot of a page without the first lines of the body (44pt is
// a three-line body, the longest that cannot be split, plus its padding).
// The card's bottom padding sits on the body text so it travels with the last
// lines; on the card itself, a body that only just fits would leave an empty
// strip of card at the top of the next page. Likewise the gap between cards
// is a top margin only: react-pdf moves a whole node to the next page when it
// fits but its bottom margin does not.
// Sections of cards need SECTION_CARD_PRESENCE below their banner.
const SECTION_CARD_PRESENCE = 90
const CARD_PADDING = 7
const NoteCard = ({ heading, meta, body, footer }) => (
  <View
    style={{
      marginHorizontal: 6,
      marginTop: 6,
      padding: CARD_PADDING,
      paddingBottom: hasText(body) ? 0 : CARD_PADDING,
      borderWidth: 0.6,
      borderColor: LINE,
      borderRadius: 3,
      backgroundColor: '#FFFFFF',
    }}
  >
    <View wrap={false} minPresenceAhead={44}>
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 3,
        }}
      >
        <Text style={{ fontSize: 8.5, color: INK, flex: 1 }}>{heading}</Text>
        {meta && <Text style={{ fontSize: 7, color: MUTED }}>{meta}</Text>}
      </View>
      {footer && <Text style={{ fontSize: 7, color: SLATE, marginBottom: 3 }}>{footer}</Text>}
    </View>
    {hasText(body) && (
      <Text style={{ fontSize: 8, color: INK, lineHeight: 1.4, paddingBottom: CARD_PADDING }}>
        {body.trim()}
      </Text>
    )}
  </View>
)

// ---------------------------------------------------------------- document

const IncidentPDF = ({ incident = {}, orgSettings = {}, printedBy }) => {
  const status = incident.status ?? 'New'
  const statusColor = STATUS_COLORS[status] ?? '#64748b'
  const characteristics = CHARACTERISTICS.filter((c) => incident?.[c.key])
  const people = incident.peopleInvolved ?? []
  const vehicles = incident.vehiclesInvolved ?? []
  const medicines = incident.relatedMedicines ?? []
  const contextMedicines = incident.medicinesInvolvedNoAction ?? []
  const witnesses = incident.witnessStatements ?? []
  const notes = incident.comments ?? []
  const attachments = incident.files ?? []
  const printedAt = moment().format('Do MMM YYYY [at] HH:mm')
  const docTitle = `Incident #${incident.incidentNumber ?? ''}`

  const investigationFields = [
    ['What happened', incident.whatHappened],
    ['Why it happened', incident.whyItHappened],
    ['How it happened', incident.howItHappened],
    ['Actions taken', incident.whatActions],
    ['Concerns identified', incident.concernsIdentified],
    ['Mitigations and positives', incident.mitigationsPositives],
    ['Investigation process', incident.investigationProcess],
    ['Root causes', incident.rootCauses],
    ['Systemic issues', incident.systemicIssues],
  ]
  const correctiveFields = [
    ['Duty of Candour', incident.dutyOfCandour],
    ['Assessed impact', incident.assessedImpact],
    ['Lessons learned', incident.lessonsLearned],
    ['Risk register additions', incident.riskRegisterAdditions],
    ['Recommendations', incident.recommendations],
    ['Actions required', incident.actionsRequired],
  ]
  const hasInvestigation =
    hasText(incident.result) ||
    hasText(incident.actualImpact) ||
    investigationFields.some(([, value]) => hasText(value))
  const noCorrectiveActions = !!incident.noCorrectiveActionsRequired
  const hasCorrective =
    noCorrectiveActions || correctiveFields.some(([, value]) => hasText(value))

  return (
    <Document
      options={{ isEvalSupported: false }}
      title={`${docTitle} — Adverse Incident Report`}
      author={orgSettings?.orgName}
    >
      <Page
        size="A4"
        style={{
          paddingTop: 36,
          paddingBottom: 48,
          paddingHorizontal: 30,
          fontFamily: 'Helvetica',
          color: INK,
        }}
      >
        {/* Running header */}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: 6,
            borderBottomWidth: 0.6,
            borderBottomColor: LINE,
            marginBottom: 12,
          }}
          fixed
        >
          {orgSettings?.logo ? (
            <Image src={orgSettings.logo} style={{ height: 24 }} />
          ) : (
            <Text style={{ fontSize: 10, color: NAVY }}>{orgSettings?.orgName}</Text>
          )}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            {orgSettings?.logo && (
              <Text style={{ fontSize: 8, color: SLATE }}>{orgSettings?.orgName}</Text>
            )}
            <Pill label="CONFIDENTIAL" color="#e11d48" filled />
          </View>
        </View>

        {/* Title block */}
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <View>
            <Text style={{ fontSize: 8, color: SLATE, letterSpacing: 0.8, textTransform: 'uppercase' }}>
              Adverse Incident Report
            </Text>
            <Text style={{ fontSize: 22, color: NAVY, marginTop: 2 }}>{docTitle}</Text>
          </View>
          <View style={{ alignItems: 'flex-end', gap: 3 }}>
            <Pill label={status} color={statusColor} filled />
            <Text style={{ fontSize: 7, color: MUTED }}>
              Reported {fmtDateTime(incident.createdAt) ?? '—'}
            </Text>
          </View>
        </View>

        {(characteristics.length > 0 || incident.cqcReport) && (
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
            {characteristics.map((c) => (
              <Pill key={c.key} label={c.label} color="#b45309" />
            ))}
            {incident.cqcReport && <Pill label="CQC Reportable" color="#e11d48" />}
          </View>
        )}

        {/* At-a-glance grid */}
        <View
          style={{
            marginTop: 10,
            backgroundColor: TINT,
            borderRadius: 4,
            flexDirection: 'row',
            flexWrap: 'wrap',
            paddingVertical: 2,
          }}
          wrap={false}
        >
          <Cell
            label="Incident date"
            value={fmtDateTime(incident.incidentDate, 'Do MMM YYYY')}
          />
          <Cell label="Time" value={fmtDateTime(incident.incidentDate, 'HH:mm')} />
          <Cell label="Location" value={incident.incidentLocation} />
          <Cell label="Type" value={incident.incidentType} />
          <Cell
            label="Harm result"
            value={incident.result}
            color={HARM_COLORS[incident.result]}
          />
          <Cell label="Actual impact" value={incident.actualImpact} />
          <Cell label="Reported by" value={fullName(incident.reportedBy)} />
          <Cell
            label="Assigned to"
            value={fullName(incident.responsibleManager?.userAccount) || 'Unassigned'}
            color={incident.responsibleManager ? INK : '#d97706'}
          />
        </View>

        {/* Confidentiality notice */}
        <View
          style={{
            marginTop: 8,
            paddingVertical: 5,
            paddingHorizontal: 8,
            borderLeftWidth: 2,
            borderLeftColor: '#e11d48',
            backgroundColor: '#FFF7F7',
          }}
          wrap={false}
        >
          <Text style={{ fontSize: 6.5, color: SLATE, lineHeight: 1.4 }}>
            This document relates to an adverse incident and may contain sensitive
            personal data, confidential organisational information and material
            relevant to internal investigations. It is provided solely to authorised
            individuals responsible for governance, safety, regulatory compliance or
            legal review, and is protected under the Data Protection Act 2018, the UK
            GDPR and applicable clinical governance standards. Unauthorised access,
            use, disclosure, copying or distribution is prohibited. If you are not the
            intended recipient, notify the sender and securely destroy this document.
          </Text>
        </View>

        {/* Incident details */}
        <Section title="Incident Details" caption="As reported">
          <Field label="Description of incident" value={incident.description} />
          <Field label="Initial actions taken" value={incident.initialActions} />
          <Field label="Related asset / equipment" value={incident.relatedAsset} />
          {!hasText(incident.description) && !hasText(incident.initialActions) && (
            <Empty>No description recorded.</Empty>
          )}
        </Section>

        {/* Involvement */}
        <Section
          title="People & Involvement"
          caption={`${people.length} ${people.length === 1 ? 'person' : 'people'} · ${vehicles.length} ${vehicles.length === 1 ? 'vehicle' : 'vehicles'}`}
        >
          {people.length > 0 ? (
            <View style={{ marginBottom: 4 }}>
              <HeaderRow columns={[['Name', 4], ['Category', 3], ['Email', 4], ['Phone', 3]]} />
              {people.map((person, index) => {
                const employee =
                  person.employee && typeof person.employee === 'object'
                    ? person.employee
                    : null
                return (
                  <Row key={person._id ?? index} striped={index % 2 === 0}>
                    <Text style={cellText(4)}>
                      {[person.firstName, person.lastName].filter(Boolean).join(' ') || 'Unnamed'}
                    </Text>
                    <Text style={cellText(3, { color: SLATE })}>
                      {employee
                        ? `Employee${employee.clinicalRole ? ` · ${employee.clinicalRole}` : ''}`
                        : person.employee
                          ? 'Employee'
                          : 'Other person'}
                    </Text>
                    <Text style={cellText(4, { color: SLATE })}>{person.email || '—'}</Text>
                    <Text style={cellText(3, { color: SLATE })}>{person.phone || '—'}</Text>
                  </Row>
                )
              })}
            </View>
          ) : (
            <Empty>No other people recorded as involved.</Empty>
          )}
          {vehicles.length > 0 && (
            <View style={{ paddingHorizontal: 6, paddingVertical: 3 }} wrap={false}>
              <Eyebrow>Vehicles involved</Eyebrow>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 4 }}>
                {vehicles.map((vehicle, index) => (
                  <Pill
                    key={vehicle?._id ?? index}
                    color={SLATE}
                    label={
                      vehicle?.callSign
                        ? `${vehicle.callSign}${vehicle.regPlate ? ` · ${vehicle.regPlate}` : ''}`
                        : 'Unknown vehicle'
                    }
                  />
                ))}
              </View>
            </View>
          )}
        </Section>

        {/* Medicines — only when relevant */}
        {(medicines.length > 0 || contextMedicines.length > 0 || incident.relatedMedsBag) && (
          <Section title="Medicines Involved">
            {incident.relatedMedsBag && (
              <Field
                label="Medicine bag / store"
                value={[
                  incident.relatedMedsBag.name,
                  incident.relatedMedsBag.type,
                  incident.relatedMedsBag.number,
                  incident.relatedMedsBag.currentLocation,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              />
            )}
            {medicines.length > 0 && (
              <View style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 8, color: NAVY, paddingHorizontal: 6, marginTop: 2 }}>
                  Stock removed as part of this incident
                </Text>
                <HeaderRow
                  columns={[['Medicine', 5], ['Presentation', 4], ['Batch', 3], ['Expiry', 2]]}
                />
                {medicines.map((item, index) => (
                  <Row key={item._id ?? index} striped={index % 2 === 0}>
                    <Text style={cellText(5)}>{item.medicine?.title ?? 'Unknown medicine'}</Text>
                    <Text style={cellText(4, { color: SLATE })}>
                      {[item.medicine?.presentation, item.medicine?.constitution]
                        .filter(Boolean)
                        .join(' · ') || '—'}
                    </Text>
                    <Text style={cellText(3, { color: SLATE })}>{item.batchNumber || '—'}</Text>
                    <Text style={cellText(2, { color: SLATE })}>
                      {fmtDateTime(item.expiry, 'MMM YYYY') ?? '—'}
                    </Text>
                  </Row>
                ))}
              </View>
            )}
            {contextMedicines.length > 0 && (
              <View style={{ paddingHorizontal: 6, paddingVertical: 3 }} wrap={false}>
                <Eyebrow>Referenced for context (no stock action)</Eyebrow>
                <Text style={{ fontSize: 8, color: INK, lineHeight: 1.4 }}>
                  {contextMedicines
                    .map((med) =>
                      [med?.title ?? 'Unknown medicine', med?.presentation]
                        .filter(Boolean)
                        .join(' '),
                    )
                    .join(', ')}
                </Text>
              </View>
            )}
            <Field label="Additional medicine information" value={incident.medicineInfo} />
          </Section>
        )}

        {/* Investigation */}
        <Section title="Investigation Findings">
          {hasInvestigation ? (
            <>
              {(hasText(incident.result) || hasText(incident.actualImpact)) && (
                <View
                  style={{ flexDirection: 'row', backgroundColor: STRIPE, marginHorizontal: 6, borderRadius: 3 }}
                  wrap={false}
                >
                  <Cell
                    label="Harm result"
                    value={incident.result}
                    color={HARM_COLORS[incident.result]}
                    width="50%"
                  />
                  <Cell label="Actual impact" value={incident.actualImpact} width="50%" />
                </View>
              )}
              {investigationFields.map(([label, value]) => (
                <Field key={label} label={label} value={value} />
              ))}
            </>
          ) : (
            <Empty>Investigation not yet recorded.</Empty>
          )}
        </Section>

        {/* Corrective actions */}
        <Section title="Corrective Actions & Learning">
          {hasCorrective ? (
            <>
              {noCorrectiveActions && (
                <Field
                  label="No corrective actions required"
                  value={
                    hasText(incident.noCorrectiveActionsReason)
                      ? incident.noCorrectiveActionsReason
                      : 'The review concluded that no corrective actions or learning were required.'
                  }
                />
              )}
              {correctiveFields.map(([label, value]) => (
                <Field key={label} label={label} value={value} />
              ))}
            </>
          ) : (
            <Empty>No corrective actions recorded yet.</Empty>
          )}
        </Section>

        {/* Witness statements */}
        {witnesses.length > 0 && (
          <Section
            title="Witness Statements"
            caption={`${witnesses.length} ${witnesses.length === 1 ? 'statement' : 'statements'}`}
            minPresenceAhead={SECTION_CARD_PRESENCE}
          >
            {witnesses.map((statement, index) => (
              <NoteCard
                key={statement._id ?? index}
                heading={statement.witnessName || 'Unnamed witness'}
                meta={fmtDateTime(statement.statementTime)}
                footer={[
                  statement.witnessInvolvement && `Involvement: ${statement.witnessInvolvement}`,
                  statement.witnessContact && `Contact: ${statement.witnessContact}`,
                  statement.source === 'external' &&
                    `Submitted by the witness via secure statement link${
                      statement.submittedAt ? ` on ${fmtDateTime(statement.submittedAt)}` : ''
                    }; declaration of truth accepted`,
                ]
                  .filter(Boolean)
                  .join('   ')}
                body={statement.witnessStatement}
              />
            ))}
          </Section>
        )}

        {/* Notes */}
        {notes.length > 0 && (
          <Section
            title="Review Notes"
            caption={`${notes.length} ${notes.length === 1 ? 'note' : 'notes'}`}
            minPresenceAhead={SECTION_CARD_PRESENCE}
          >
            {notes.map((note, index) => (
              <NoteCard
                key={note._id ?? index}
                heading={fullName(note.by) || 'Unknown'}
                meta={fmtDateTime(note.createdAt)}
                body={note.comment}
              />
            ))}
          </Section>
        )}

        {/* Attachments */}
        {attachments.length > 0 && (
          <Section title="Attachments" caption={`${attachments.length} on file`}>
            <Empty>
              {attachments.length} {attachments.length === 1 ? 'file is' : 'files are'} attached
              to this incident in AmbuNet and can be viewed on the incident record.
            </Empty>
          </Section>
        )}

        {/* Sign-off line */}
        <View
          style={{
            marginTop: 16,
            paddingTop: 6,
            borderTopWidth: 0.6,
            borderTopColor: LINE,
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
          wrap={false}
        >
          <Text style={{ fontSize: 7, color: MUTED }}>
            Generated by {printedBy || 'AmbuNet'} on {printedAt}
          </Text>
          <Text style={{ fontSize: 7, color: MUTED }}>
            Last updated {fmtDateTime(incident.updatedAt) ?? '—'}
          </Text>
        </View>

        <View
          style={{ marginTop: 6 }}
          render={({ pageNumber, totalPages }) =>
            pageNumber === totalPages ? (
              <Text style={{ fontSize: 6.5, color: MUTED, lineHeight: 1.4 }}>
                Powered by AmbuNet®. AmbuNet provides the technical platform for
                creating and accessing this incident record and is not responsible
                for the accuracy, completeness or interpretation of the information
                entered, nor for any actions or decisions taken as a result.
                Responsibility for the correct, lawful and authorised use of this
                report rests with the submitting user and the issuing organisation.
              </Text>
            ) : null
          }
        />

        {/* Running footer */}
        <Text
          style={{
            position: 'absolute',
            bottom: 22,
            left: 30,
            right: 30,
            fontSize: 7,
            color: MUTED,
            textAlign: 'center',
          }}
          render={({ pageNumber, totalPages }) =>
            `${orgSettings?.orgName ?? ''} — ${docTitle} — Confidential — page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  )
}

export default IncidentPDF
